import { getOperatingDateStr } from '../../lib/utils';
import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, 
  BarChart3, 
  DollarSign, 
  Clock, 
  Calendar, 
  Users, 
  Award, 
  ArrowUpRight, 
  Activity, 
  Filter, 
  Loader2,
  CalendarDays,
  Percent,
  CheckCircle,
  XCircle,
  HelpCircle,
  Info,
  ShoppingBag,
  Package,
  Repeat,
  Coffee,
  LineChart,
  Pin,
  ChevronRight,
  X,
  Download
} from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { Booking, BookingRequest, Player, UserProfile, ClubSchedule } from '../../types';

interface AdminAnalyticsProps {
  userId: string; // Sede logueada
  userRole: 'user' | 'admin' | 'superadmin' | 'co_admin' | 'empleado' | 'gestor_turnos' | 'gestor_torneos';
  players: Player[];
  sedesPublicas: UserProfile[];
  db: any;
  getCollectionPath: (col: string) => string;
}

type DateRange = '1day' | '7days' | '30days' | 'all' | 'custom';

function AnimatedNumber({ value, formatter, duration = 1500 }: { value: number, formatter?: (val: number) => string | number, duration?: number }) {
  const [displayValue, setDisplayValue] = React.useState(0);
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });

  React.useEffect(() => {
    if (!inView) return;
    let startTimestamp: number;
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(ease * value));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [inView, value, duration]);

  return <span ref={ref}>{formatter ? formatter(displayValue) : displayValue}</span>;
}

export function AdminAnalytics({
  userId,
  userRole,
  players,
  sedesPublicas,
  db,
  getCollectionPath
}: AdminAnalyticsProps) {
  // --- ESTADOS ---
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [requests, setRequests] = useState<BookingRequest[]>([]);
  const [schedules, setSchedules] = useState<Record<string, ClubSchedule>>({});
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState<DateRange>('30days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedSedeId, setSelectedSedeId] = useState<string>(userRole === 'superadmin' ? 'all' : userId);
  
  const [archivedBookings, setArchivedBookings] = useState<Booking[]>([]);
  const [hasFetchedArchived, setHasFetchedArchived] = useState(false);

  useEffect(() => {
    // Si el usuario elige un rango que podría requerir datos viejos, cargamos el archivo una sola vez
    if ((selectedRange === 'all' || selectedRange === 'custom') && !hasFetchedArchived) {
      const fetchArchived = async () => {
        try {
          const archCol = collection(db, getCollectionPath('archived_bookings'));
          const snap = await getDocs(archCol);
          const list: Booking[] = [];
          snap.forEach(d => list.push({ id: d.id, ...d.data() } as Booking));
          setArchivedBookings(list);
          setHasFetchedArchived(true);
        } catch (e) {
          console.error("Error al cargar historial archivado:", e);
        }
      };
      fetchArchived();
    }
  }, [selectedRange, hasFetchedArchived, db, getCollectionPath]);

  // Estado para tooltips e interactividad del gráfico principal
  const [hoveredPointIdx, setHoveredPointIdx] = useState<number | null>(null);
  const [hoveredHour, setHoveredHour] = useState<string | null>(null);
  const [showFidelityModal, setShowFidelityModal] = useState(false);
  const [chartFilters, setChartFilters] = useState({
    total: true,
    cantina: true,
    turnos: true
  });

  // --- ESCUCHAR DATOS EN TIEMPO REAL ---
  useEffect(() => {
    setLoading(true);

    const bookingsCol = collection(db, getCollectionPath('bookings'));
    const unsubBookings = onSnapshot(bookingsCol, (snapshot) => {
      const list: Booking[] = [];
      snapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() } as Booking);
      });
      setBookings(list);
      setLoading(false);
    }, (err) => {
      console.error("Error al escuchar reservas para analíticas:", err);
      setLoading(false);
    });

    const requestsCol = collection(db, getCollectionPath('bookingRequests'));
    const unsubRequests = onSnapshot(requestsCol, (snapshot) => {
      const list: BookingRequest[] = [];
      snapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() } as BookingRequest);
      });
      setRequests(list);
    }, (err) => {
      console.error("Error al escuchar solicitudes para analíticas:", err);
    });

    const schedulesCol = collection(db, getCollectionPath('clubSchedules'));
    const unsubSchedules = onSnapshot(schedulesCol, (snapshot) => {
      const dict: Record<string, ClubSchedule> = {};
      snapshot.forEach((docSnap) => {
        dict[docSnap.id] = { id: docSnap.id, ...docSnap.data() } as ClubSchedule;
      });
      setSchedules(dict);
    }, (err) => {
      console.error("Error al escuchar esquemas de clubes:", err);
    });

    return () => {
      unsubBookings();
      unsubRequests();
      unsubSchedules();
    };
  }, [db, getCollectionPath]);

  // --- RESOLVER LISTA DE SEDES ---
  const activeSedes = useMemo(() => {
    return sedesPublicas.filter(s => s.role === 'admin');
  }, [sedesPublicas]);

  // --- FILTRAR RESERVAS Y SOLICITUDES POR FECHA Y SEDE ---
  const { filteredBookings, filteredRequests, daysCount } = useMemo(() => {
    let listB = [...bookings, ...archivedBookings];
    let listR = requests;

    if (selectedSedeId !== 'all') {
      listB = listB.filter(b => b.clubId === selectedSedeId);
      listR = listR.filter(r => r.clubId === selectedSedeId);
    }

    const now = new Date();
    
    // Función auxiliar para formatear la fecha local sin desfases UTC (GMT-3)
    const getLocalDateString = (date: Date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const schedule = selectedSedeId !== 'all' ? schedules[selectedSedeId] : Object.values(schedules)[0];
    const todayStr = getOperatingDateStr(now, schedule?.cashRegisterCloseTime || '03:30');
    let startLimitStr = '';
    let endLimitStr = '';
    let dCount = 30;

    if (selectedRange === '1day') {
      startLimitStr = todayStr;
      endLimitStr = todayStr;
      dCount = 1;
    } else if (selectedRange === '7days') {
      const limit = new Date(now);
      limit.setDate(now.getDate() - 7);
      startLimitStr = getLocalDateString(limit);
      endLimitStr = todayStr;
      dCount = 7;
    } else if (selectedRange === '30days') {
      const limit = new Date(now);
      limit.setDate(now.getDate() - 30);
      startLimitStr = getLocalDateString(limit);
      endLimitStr = todayStr;
      dCount = 30;
    } else if (selectedRange === 'custom') {
      startLimitStr = customStartDate;
      endLimitStr = customEndDate;
      if (startLimitStr && endLimitStr) {
        const sDate = new Date(startLimitStr + 'T12:00:00');
        const eDate = new Date(endLimitStr + 'T12:00:00');
        const diffTime = Math.abs(eDate.getTime() - sDate.getTime());
        dCount = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1);
      } else {
        dCount = 30; // fallback
      }
    } else {
      // Histórico: Muestra todo, sin límites superiores ni inferiores
      dCount = 365;
      const dates = bookings.map(b => b.date).filter(Boolean);
      if (dates.length > 0) {
        dates.sort();
        const firstDate = new Date(dates[0] + 'T12:00:00');
        const diffTime = Math.abs(now.getTime() - firstDate.getTime());
        dCount = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
      }
    }

    if (selectedRange !== 'all' && startLimitStr && endLimitStr) {
      listB = listB.filter(b => b.date >= startLimitStr && b.date <= endLimitStr);
      listR = listR.filter(r => r.date >= startLimitStr && r.date <= endLimitStr);
    }

    return { filteredBookings: listB, filteredRequests: listR, daysCount: dCount };
  }, [bookings, requests, selectedRange, selectedSedeId, customStartDate, customEndDate]);

  // --- CÁLCULO DE MÉTRICAS GLOBALES ---
  const stats = useMemo(() => {
    // Desglosar ingresos: cancha vs cantina y formas de pago
    let courtRevenue = 0;
    let cantinaRevenue = 0;
    let cashRevenue = 0;
    let transferRevenue = 0;
    let operationalExpenses = 0;
    let topExpenses: Record<string, number> = {};
    let totalBookings = 0;
    let fixedBookingsCount = 0;

    filteredBookings.forEach(b => {
      if (b.courtId === 'expense') {
        const amount = Math.abs(b.price || 0);
        if (b.courtName !== 'Retiro de Socio' && b.courtName !== 'Depósito Bancario' && b.courtName !== 'Retiro de Dinero') {
          operationalExpenses += amount;
          if (b.courtName) topExpenses[b.courtName] = (topExpenses[b.courtName] || 0) + amount;
        }
        return; // Exclude from revenue
      }

      if (b.status === 'paid') {
        let base = b.price || 0;
        let cantina = 0;
        
        if (b.courtId === 'pos_cantina') {
           cantina = base;
           base = 0;
        } else {
           cantina = b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0;
        }

        let cash = 0;
        let transfer = 0;

        if (b.paymentBreakdown) {
          cash = b.paymentBreakdown.cash || 0;
          transfer = b.paymentBreakdown.transfer || 0;
          
          // Si hubo modificación manual del total cobrado
          const actualPaid = cash + transfer;
          if (actualPaid !== (base + cantina)) {
            if (b.courtId === 'pos_cantina') {
              cantina = actualPaid;
            } else {
              base = actualPaid - cantina;
            }
          }
        } else {
          transfer = base + cantina;
        }

        courtRevenue += base;
        cantinaRevenue += cantina;
        cashRevenue += cash;
        transferRevenue += transfer;
      }

      if ((b.status === 'confirmed' || b.status === 'paid') && b.courtId !== 'pos_cantina') {
        totalBookings++;
        if (b.isFixed) fixedBookingsCount++;
      }
    });

    const totalRevenue = courtRevenue + cantinaRevenue;
    const netBalance = totalRevenue - operationalExpenses;
    
    const courtPct = totalRevenue > 0 ? Math.round((courtRevenue / totalRevenue) * 100) : 0;
    const cantinaPct = totalRevenue > 0 ? Math.round((cantinaRevenue / totalRevenue) * 100) : 0;
    const cashPct = totalRevenue > 0 ? Math.round((cashRevenue / totalRevenue) * 100) : 0;
    const transferPct = totalRevenue > 0 ? Math.round((transferRevenue / totalRevenue) * 100) : 0;

    const expensesList = Object.entries(topExpenses).map(([name, amount]) => ({ name, amount })).sort((a,b) => b.amount - a.amount);
    
    // Players (excluding cantina/expense)
    const validBookings = filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense');
    const uniquePlayers = new Set(validBookings.map(b => b.playerId).filter(Boolean));
    const uniquePlayersCount = uniquePlayers.size;

    const totalReq = filteredRequests.length;
    const approvedReq = filteredRequests.filter(r => r.status === 'accepted').length;
    const rejectedReq = filteredRequests.filter(r => r.status === 'rejected').length;
    const pendingReq = filteredRequests.filter(r => r.status === 'pending').length;
    const approvalRate = totalReq > 0 ? Math.round((approvedReq / totalReq) * 100) : 100;

    // Calcular Capacidad de Turnos
    let totalCapacitySlots = 0;
    if (selectedSedeId !== 'all') {
      const sched = schedules[selectedSedeId];
      const courtsCount = sched?.courts?.length || 3;
      let slotsPerDay = 10;
      if (sched?.openTime && sched?.closeTime) {
        const [oH, oM] = sched.openTime.split(':').map(Number);
        const [cH, cM] = sched.closeTime.split(':').map(Number);
        let minutesOpen = (cH * 60 + cM) - (oH * 60 + oM);
        if (minutesOpen < 0) minutesOpen += 24 * 60;
        const duration = sched.slotDuration || 90;
        slotsPerDay = Math.max(1, Math.floor(minutesOpen / duration));
      }
      totalCapacitySlots = courtsCount * slotsPerDay * daysCount;
    } else {
      const targetSedes = Object.keys(schedules).length > 0 ? Object.keys(schedules) : ['default'];
      targetSedes.forEach(sId => {
        const sched = schedules[sId];
        const courtsCount = sched?.courts?.length || 3;
        let slotsPerDay = 10;
        if (sched?.openTime && sched?.closeTime) {
          const [oH, oM] = sched.openTime.split(':').map(Number);
          const [cH, cM] = sched.closeTime.split(':').map(Number);
          let minutesOpen = (cH * 60 + cM) - (oH * 60 + oM);
          if (minutesOpen < 0) minutesOpen += 24 * 60;
          const duration = sched.slotDuration || 90;
          slotsPerDay = Math.max(1, Math.floor(minutesOpen / duration));
        }
        totalCapacitySlots += (courtsCount * slotsPerDay * daysCount);
      });
    }

    const occupancyRate = totalCapacitySlots > 0 
      ? Math.min(100, Math.round((totalBookings / totalCapacitySlots) * 100))
      : totalBookings > 0 ? 45 : 0;

    return {
      totalRevenue,
      netBalance,
      operationalExpenses,
      expensesList,
      courtRevenue,
      cantinaRevenue,
      cashRevenue,
      transferRevenue,
      courtPct,
      cantinaPct,
      cashPct,
      transferPct,
      totalBookings,
      fixedBookingsCount,
      uniquePlayersCount,
      totalReq,
      approvedReq,
      rejectedReq,
      pendingReq,
      approvalRate,
      occupancyRate,
      totalCapacitySlots
    };
  }, [filteredBookings, filteredRequests, schedules, selectedSedeId, daysCount]);

  // --- DATOS TEMPORALES AGREGADOS PARA EL GRÁFICO PRINCIPAL ---
  const trendData = useMemo(() => {
    const map: Record<string, { revenue: number; cantinaRevenue: number; courtRevenue: number }> = {};
    const now = new Date();

    if (selectedRange === '1day') {
      // Agrupar por franja horaria para Hoy
      const hours = ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'];
      hours.forEach(h => {
        map[h] = { revenue: 0, cantinaRevenue: 0, courtRevenue: 0 };
      });

      filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense').forEach(b => {
      if (b.timeSlot) {
          const slotHour = parseInt(b.timeSlot.split(':')[0], 10);
          // Agrupar en el intervalo más cercano
          const matchedHour = hours.find(h => {
            const hHour = parseInt(h.split(':')[0], 10);
            return Math.abs(slotHour - hHour) <= 1;
          }) || '18:00';
          
          if (b.status === 'paid') {
            const base = b.price || 0;
            const itemsSum = b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0;
            map[matchedHour].revenue += (base + itemsSum);
            map[matchedHour].cantinaRevenue += itemsSum;
            map[matchedHour].courtRevenue += base;
          }
        }
      });

      return hours.map(label => ({
        label,
        revenue: map[label].revenue,
        cantinaRevenue: map[label].cantinaRevenue,
        courtRevenue: map[label].courtRevenue
      }));
    } else if (selectedRange === '7days') {
      // Agrupar los últimos 7 días
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(now.getDate() - i);
        const label = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
        const key = d.toISOString().split('T')[0];
        map[key] = { revenue: 0, cantinaRevenue: 0, courtRevenue: 0 };
      }

      filteredBookings.forEach(b => {
        if (b.date && map[b.date] !== undefined) {
          if (b.status === 'paid') {
            const base = b.price || 0;
            const itemsSum = b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0;
            map[b.date].revenue += (base + itemsSum);
            map[b.date].cantinaRevenue += itemsSum;
            map[b.date].courtRevenue += base;
          }
        }
      });

      return Object.entries(map)
        .map(([key, val]) => {
          const d = new Date(key + 'T12:00:00');
          const label = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
          return { label, ...val };
        });
    } else {
      // Agrupar últimos 30 días o histórico por fechas con actividad
      filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense').forEach(b => {
      if (b.date) {
          const [y, m, d] = b.date.split('-');
          const label = `${d}/${m}`;
          if (!map[label]) map[label] = { revenue: 0, cantinaRevenue: 0, courtRevenue: 0 };
          if (b.status === 'paid') {
            const base = b.price || 0;
            const itemsSum = b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0;
            map[label].revenue += (base + itemsSum);
            map[label].cantinaRevenue += itemsSum;
            map[label].courtRevenue += base;
          }
        }
      });

      const list = Object.entries(map).map(([label, val]) => {
        const [d, m] = label.split('/');
        return { label, sortKey: `${m}-${d}`, ...val };
      }).sort((a, b) => a.sortKey.localeCompare(b.sortKey));

      return list.map(({ label, revenue, cantinaRevenue, courtRevenue }) => ({ label, revenue, cantinaRevenue, courtRevenue }));
    }
  }, [filteredBookings, selectedRange]);

  // --- DATOS SIMULADOS PARA DEMOSTRACIÓN CUANDO NO HAY SUFICIENTES DATOS ---
  const isDemoMode = trendData.length === 0 || trendData.every(d => d.revenue === 0 && d.courtRevenue === 0 && d.cantinaRevenue === 0);
  
  const finalTrendData = useMemo(() => {
    if (!isDemoMode) return trendData;

    // Crear una curva hermosa de demostración que ilustre ingresos típicos
    if (selectedRange === '1day') {
      return [
        { label: '08:00', revenue: 15000, cantinaRevenue: 3000, courtRevenue: 12000 },
        { label: '10:00', revenue: 30000, cantinaRevenue: 5000, courtRevenue: 25000 },
        { label: '12:00', revenue: 15000, cantinaRevenue: 2000, courtRevenue: 13000 },
        { label: '14:00', revenue: 0, cantinaRevenue: 0, courtRevenue: 0 },
        { label: '16:00', revenue: 45000, cantinaRevenue: 10000, courtRevenue: 35000 },
        { label: '18:00', revenue: 90000, cantinaRevenue: 20000, courtRevenue: 70000 },
        { label: '20:00', revenue: 120000, cantinaRevenue: 30000, courtRevenue: 90000 },
        { label: '22:00', revenue: 45000, cantinaRevenue: 8000, courtRevenue: 37000 }
      ];
    } else if (selectedRange === '7days') {
      return [
        { label: 'Lun', revenue: 45000, cantinaRevenue: 8000, courtRevenue: 37000 },
        { label: 'Mar', revenue: 60000, cantinaRevenue: 12000, courtRevenue: 48000 },
        { label: 'Mié', revenue: 90000, cantinaRevenue: 20000, courtRevenue: 70000 },
        { label: 'Jue', revenue: 75000, cantinaRevenue: 15000, courtRevenue: 60000 },
        { label: 'Vie', revenue: 150000, cantinaRevenue: 40000, courtRevenue: 110000 },
        { label: 'Sáb', revenue: 180000, cantinaRevenue: 50000, courtRevenue: 130000 },
        { label: 'Dom', revenue: 105000, cantinaRevenue: 25000, courtRevenue: 80000 }
      ];
    } else {
      return [
        { label: 'Semana 1', revenue: 120000, cantinaRevenue: 25000, courtRevenue: 95000 },
        { label: 'Semana 2', revenue: 240000, cantinaRevenue: 60000, courtRevenue: 180000 },
        { label: 'Semana 3', revenue: 180000, cantinaRevenue: 40000, courtRevenue: 140000 },
        { label: 'Semana 4', revenue: 310000, cantinaRevenue: 80000, courtRevenue: 230000 }
      ];
    }
  }, [trendData, isDemoMode, selectedRange]);

  // --- CÁLCULOS MATEMÁTICOS PARA EL SVG VECTORIAL DEL GRÁFICO ---
  const chartSvgData = useMemo(() => {
    const width = 800;
    const height = 280;
    const paddingLeft = 70;
    const paddingRight = 40;
    const paddingTop = 40;
    const paddingBottom = 40;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const maxRevenue = Math.max(...finalTrendData.map(d => d.revenue), 10000);

    const points = finalTrendData.map((d, idx) => {
      const x = paddingLeft + (idx / Math.max(1, finalTrendData.length - 1)) * chartWidth;
      const yRev = height - paddingBottom - (d.revenue / maxRevenue) * chartHeight;
      const yCantina = height - paddingBottom - (d.cantinaRevenue / maxRevenue) * chartHeight;
      const yCourt = height - paddingBottom - (d.courtRevenue / maxRevenue) * chartHeight;
      return { x, yRev, yCantina, yCourt, label: d.label, revenue: d.revenue, cantinaRevenue: d.cantinaRevenue, courtRevenue: d.courtRevenue };
    });

    let revLinePath = '';
    let revAreaPath = '';
    let cantinaLinePath = '';
    let courtLinePath = '';

    if (points.length === 1) {
      revLinePath = `M ${paddingLeft} ${points[0].yRev} L ${width - paddingRight} ${points[0].yRev}`;
      revAreaPath = `M ${paddingLeft} ${points[0].yRev} L ${width - paddingRight} ${points[0].yRev} L ${width - paddingRight} ${height - paddingBottom} L ${paddingLeft} ${height - paddingBottom} Z`;
      cantinaLinePath = `M ${paddingLeft} ${points[0].yCantina} L ${width - paddingRight} ${points[0].yCantina}`;
      courtLinePath = `M ${paddingLeft} ${points[0].yCourt} L ${width - paddingRight} ${points[0].yCourt}`;
    } else if (points.length > 1) {
      const revCoords = points.map(p => `${p.x},${p.yRev}`).join(' L ');
      revLinePath = `M ${revCoords}`;
      revAreaPath = `M ${points[0].x},${height - paddingBottom} L ${revCoords} L ${points[points.length - 1].x},${height - paddingBottom} Z`;
      cantinaLinePath = `M ${points.map(p => `${p.x},${p.yCantina}`).join(' L ')}`;
      courtLinePath = `M ${points.map(p => `${p.x},${p.yCourt}`).join(' L ')}`;
    }

    return {
      width,
      height,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      chartWidth,
      chartHeight,
      points,
      revLinePath,
      revAreaPath,
      cantinaLinePath,
      courtLinePath,
      maxRevenue
    };
  }, [finalTrendData]);

  // --- DISTRIBUCIÓN POR CANCHA ---
  const courtDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredBookings.forEach(b => {
      if (b.courtName && b.courtId !== 'pos_cantina' && b.courtId !== 'expense') {
        counts[b.courtName] = (counts[b.courtName] || 0) + 1;
      }
    });

    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    })).sort((a, b) => b.count - a.count);
  }, [filteredBookings]);

  // --- DÍAS DE LA SEMANA MÁS POPULARES ---
  const weekdayDistribution = useMemo(() => {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    const names = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    
    filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense').forEach(b => {
      if (b.date) {
        const [y, m, d] = b.date.split('-').map(Number);
        const dayIdx = new Date(y, m - 1, d).getDay();
        counts[dayIdx] = counts[dayIdx] + 1;
      }
    });

    const maxVal = Math.max(...counts, 1);
    const total = counts.reduce((a, b) => a + b, 0);

    const reorderedIndices = [1, 2, 3, 4, 5, 6, 0]; 
    return reorderedIndices.map(idx => ({
      name: names[idx],
      count: counts[idx],
      percentage: total > 0 ? Math.round((counts[idx] / total) * 100) : 0,
      scale: (counts[idx] / maxVal) * 100
    }));
  }, [filteredBookings]);

  // --- HORAS DE MAYOR DEMANDA ---
  const hourDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense').forEach(b => {
      if (b.timeSlot) {
        const formatted = b.timeSlot.includes(':') ? b.timeSlot : `${b.timeSlot.slice(0,2)}:${b.timeSlot.slice(2)}`;
        counts[formatted] = (counts[formatted] || 0) + 1;
      }
    });

    const sortedHours = Object.keys(counts).sort((a,b) => {
      const [ha, ma] = a.split(':').map(Number);
      const [hb, mb] = b.split(':').map(Number);
      return (ha * 60 + ma) - (hb * 60 + mb);
    });

    const maxVal = Math.max(...Object.values(counts), 1);
    
    return sortedHours.map(hour => ({
      hour,
      count: counts[hour],
      scale: (counts[hour] / maxVal) * 100
    }));
  }, [filteredBookings]);

  // --- CÁLCULOS SVG PARA EL GRÁFICO DE HORAS (NUEVO DISEÑO) ---
  const hourSvgData = useMemo(() => {
    if (hourDistribution.length === 0) return null;
    
    const width = 800;
    const height = 180;
    const paddingX = 40;
    const paddingY = 20;
    
    const chartWidth = width - (paddingX * 2);
    const chartHeight = height - (paddingY * 2);
    
    const points = hourDistribution.map((d, idx) => {
      const x = paddingX + (idx / Math.max(1, hourDistribution.length - 1)) * chartWidth;
      const y = height - paddingY - (d.scale / 100) * chartHeight;
      return { x, y, ...d };
    });

    // Crear path curvo (Bézier cuadrático simple para suavidad)
    let path = `M ${points[0].x} ${points[0].y}`;
    let areaPath = `M ${points[0].x} ${height - paddingY} L ${points[0].x} ${points[0].y}`;
    
    for (let i = 0; i < points.length - 1; i++) {
      const curr = points[i];
      const next = points[i + 1];
      const mx = (curr.x + next.x) / 2;
      path += ` Q ${curr.x} ${curr.y}, ${mx} ${(curr.y + next.y) / 2}`;
      areaPath += ` Q ${curr.x} ${curr.y}, ${mx} ${(curr.y + next.y) / 2}`;
    }
    
    const last = points[points.length - 1];
    path += ` L ${last.x} ${last.y}`;
    areaPath += ` L ${last.x} ${last.y} L ${last.x} ${height - paddingY} Z`;

    return { points, path, areaPath, width, height };
  }, [hourDistribution]);

  // --- JUGADORES ESTRELLA (TOP 5) ---
  const topPlayers = useMemo(() => {
    const map: Record<string, { name: string; photo: string; count: number; spend: number }> = {};
    
    filteredBookings.filter(b => b.courtId !== 'pos_cantina' && b.courtId !== 'expense').forEach(b => {
      if (b.playerId) {
        if (!map[b.playerId]) {
          map[b.playerId] = {
            name: b.playerName || 'Jugador Anónimo',
            photo: b.playerPhoto || '',
            count: 0,
            spend: 0
          };
        }
        map[b.playerId].count += 1;
        const base = b.price || 0;
        const itemsSum = b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0;
        map[b.playerId].spend += (base + itemsSum);
      }
    });

    return Object.entries(map)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [filteredBookings]);

  // --- DESGLOSE DE PRODUCTOS Y CANTINA ---
  const { topProducts, totalProductsRevenue, totalProductsSold } = useMemo(() => {
    const map: Record<string, { name: string; count: number; revenue: number }> = {};
    let revenue = 0;
    let sold = 0;

    filteredBookings.forEach(b => {
      if (b.status === 'paid' || b.status === 'confirmed') {
        b.items?.forEach(item => {
          if (!map[item.name]) {
            map[item.name] = { name: item.name, count: 0, revenue: 0 };
          }
          map[item.name].count += item.quantity;
          const itemRev = item.price * item.quantity;
          map[item.name].revenue += itemRev;
          
          if (b.status === 'paid') {
            revenue += itemRev;
          }
          sold += item.quantity;
        });
      }
    });

    const list = Object.values(map).sort((a, b) => b.count - a.count);

    return {
      topProducts: list,
      totalProductsRevenue: revenue,
      totalProductsSold: sold
    };
  }, [filteredBookings]);

  // --- FORMATEADOR DE MONEDA ---
  const formatMoney = (val: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(val);
  };

  // --- EXPORTAR A EXCEL (STYLED) ---
  const handleExportExcel = async () => {
    // Importación dinámica para que no pese en el bundle inicial si no es necesario
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'App del Club';
    workbook.created = new Date();
    
    const worksheet = workbook.addWorksheet('Reporte de Ingresos');

    // 1. Definir columnas
    worksheet.columns = [
      { header: 'Fecha', key: 'fecha', width: 12 },
      { header: 'Hora', key: 'hora', width: 10 },
      { header: 'Concepto / Cancha', key: 'cancha', width: 28 },
      { header: 'Detalle / Jugador', key: 'jugador', width: 25 },
      { header: 'Ingreso Turno', key: 'ingreso_turno', width: 16 },
      { header: 'Ingreso Cantina', key: 'ingreso_cantina', width: 16 },
      { header: 'Total Ingreso', key: 'total_ingreso', width: 16 },
      { header: 'Gasto Operativo', key: 'gasto_operativo', width: 16 },
      { header: 'Pagado en Efectivo', key: 'efectivo', width: 20 },
      { header: 'Pagado Transferencia', key: 'transferencia', width: 22 },
      { header: 'Estado', key: 'estado', width: 15 },
    ];

    // Estilo a la cabecera (Fila 1)
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } }; // Verde oscuro elegante
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.height = 30;
    
    // ORDENAR: Primero por fecha, luego por hora
    const sortedBookings = [...filteredBookings].sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      
      const timeA = a.timeSlot || '';
      const timeB = b.timeSlot || '';
      return timeA.localeCompare(timeB);
    });
    
    // 2. Agregar datos (Ingresos y Egresos) con diseño "Zebra"
    sortedBookings.forEach((b, index) => {
      const isExpense = b.courtId === 'expense';
      const basePrice = Math.abs(b.price || 0);
      
      let cantinaTotal = 0;
      let courtCost = 0;
      let totalIngreso = 0;
      let gastoOperativo = 0;
      
      if (isExpense) {
        gastoOperativo = basePrice;
      } else if (b.courtId === 'pos_cantina') {
        cantinaTotal = basePrice;
        totalIngreso = basePrice;
      } else {
        cantinaTotal = b.items?.reduce((acc, item) => acc + (item.price * item.quantity), 0) || 0;
        courtCost = basePrice;
        totalIngreso = courtCost + cantinaTotal;
      }
      
      let cash = 0;
      let transfer = 0;
      
      if (b.status === 'paid' || isExpense) {
        if (b.paymentBreakdown) {
          cash = b.paymentBreakdown.cash || 0;
          transfer = b.paymentBreakdown.transfer || 0;
          
          // Ajustar ingresos si hubo modificación manual
          if (!isExpense) {
            const actualPaid = cash + transfer;
            if (actualPaid !== (courtCost + cantinaTotal)) {
              if (b.courtId === 'pos_cantina') {
                cantinaTotal = actualPaid;
              } else {
                courtCost = actualPaid - cantinaTotal;
              }
            }
          }
        } else {
          transfer = isExpense ? gastoOperativo : (courtCost + cantinaTotal);
        }
      }
      
      if (!isExpense) {
        totalIngreso = courtCost + cantinaTotal;
      }

      const row = worksheet.addRow({
        fecha: b.date ? b.date.split('-').reverse().join('/') : '', // Formato DD/MM/YYYY
        hora: b.timeSlot ? `${b.timeSlot}hs` : '',
        cancha: b.courtName || 'N/A',
        jugador: b.playerName || '-',
        ingreso_turno: courtCost > 0 ? courtCost : 0,
        ingreso_cantina: cantinaTotal > 0 ? cantinaTotal : 0,
        total_ingreso: totalIngreso > 0 ? totalIngreso : 0,
        gasto_operativo: gastoOperativo > 0 ? gastoOperativo : 0,
        efectivo: cash,
        transferencia: transfer,
        estado: b.status === 'paid' ? 'Pagado' : b.status === 'confirmed' ? 'Confirmado' : b.status || 'Completado'
      });

      // Efecto Cebra (Filas grises intercaladas)
      if (index % 2 !== 0) {
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } }; // Gris muy clarito
      }
      
      row.alignment = { vertical: 'middle', horizontal: 'center' };

      // Estilo de números y moneda
      const numCols = [5, 6, 7, 8, 9, 10];
      numCols.forEach(colIndex => {
        const cell = row.getCell(colIndex);
        cell.numFmt = '"$"#,##0.00';
        if (cell.value === 0) {
          cell.font = { color: { argb: 'FFD1D5DB' } }; // Gris claro si es 0
        } else {
          cell.font = { bold: true, color: { argb: 'FF1F2937' } };
        }
      });
      
      // Color rojo a los gastos
      if (isExpense) {
        const expenseCell = row.getCell(8);
        expenseCell.font = { color: { argb: 'FFDC2626' }, bold: true };
      }
      
      // Alineación a la izquierda para cancha y jugador
      row.getCell(3).alignment = { horizontal: 'left' };
      row.getCell(4).alignment = { horizontal: 'left' };
    });

    // 3. Agregar Resumen Financiero
    worksheet.addRow([]);
    worksheet.addRow([]);
    
    const summaryTitleRow = worksheet.addRow(['RESUMEN FINANCIERO DEL PERIODO']);
    summaryTitleRow.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    summaryTitleRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF111827' } }; // Negro suave
    worksheet.mergeCells(`A${summaryTitleRow.number}:D${summaryTitleRow.number}`);
    summaryTitleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    summaryTitleRow.height = 25;

    const addSummaryRow = (label: string, value: number | string, isBold: boolean = false, colorBg?: string, colorText?: string) => {
      const r = worksheet.addRow([label, '', '', value]);
      worksheet.mergeCells(`A${r.number}:C${r.number}`);
      if (typeof value === 'number') {
        r.getCell(4).numFmt = '"$"#,##0.00';
      }
      r.getCell(4).alignment = { horizontal: 'right' };
      r.getCell(1).alignment = { horizontal: 'left' };
      
      if (isBold) r.font = { bold: true };
      if (colorText) r.font = { ...r.font, color: { argb: colorText } };
      if (colorBg) r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorBg } };
      
      // Borde sutil
      r.border = { bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
    };

    addSummaryRow('Ingresos Brutos Totales', stats.totalRevenue);
    addSummaryRow('Gastos Operativos', stats.operationalExpenses, false, undefined, 'FFDC2626');
    addSummaryRow('INGRESO NETO', stats.netBalance, true, 'FF10B981', 'FFFFFFFF');
    worksheet.addRow([]);
    
    addSummaryRow('Desglose de Ingresos', '', true, 'FFF3F4F6');
    addSummaryRow('Alquiler de Canchas', stats.courtRevenue);
    addSummaryRow('Consumos de Cantina', stats.cantinaRevenue);
    worksheet.addRow([]);
    
    addSummaryRow('Desglose por Métodos de Pago', '', true, 'FFF3F4F6');
    addSummaryRow('Cobrado en Efectivo', stats.cashRevenue);
    addSummaryRow('Cobrado por Transferencia', stats.transferRevenue);

    // 4. Descargar
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const dateStr = selectedRange === 'custom' ? `${customStartDate}_al_${customEndDate}` : selectedRange;
    saveAs(blob, `Balance_Financiero_${dateStr}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <Loader2 className="w-12 h-12 theme-primary-text animate-spin mb-4" />
        <p className="text-white/60 font-medium">Cargando métricas y estadísticas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* 1. HEADER Y FILTROS INTEGRADOS */}
      <div className="relative w-full">
        <div className="flex flex-col items-center justify-center gap-6 relative z-10 w-full">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-4 mb-4 md:-translate-x-8">
              {/* Línea divisoria izquierda */}
              <div className="hidden md:block h-[1px] w-12 md:w-32 bg-gradient-to-r from-transparent to-white/30" />
              
              <div className="p-2.5 rounded-2xl bg-white/5 border border-white/10 text-white shadow-sm">
                <BarChart3 className="w-7 h-7 md:w-8 md:h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white tracking-tight uppercase">
                Métricas y Analíticas
              </h1>

              {/* Línea divisoria derecha */}
              <div className="hidden md:block h-[1px] w-12 md:w-32 bg-gradient-to-l from-transparent to-white/30" />
            </div>
          </div>

          {/* BANNER DE FILTROS SUPER INTUITIVO */}
          <div className="flex flex-col items-center gap-3 w-full">
            <div className="flex flex-wrap items-center justify-center gap-3 w-full">
              
              {/* Selector de Sede para el Súper Admin */}
              {userRole === 'superadmin' && (
                <div className="flex items-center gap-2 bg-black/60 border border-white/5 rounded-xl px-3 py-2 text-white">
                  <Filter className="w-3.5 h-3.5 text-white/40" />
                  <select
                    value={selectedSedeId}
                    onChange={(e) => setSelectedSedeId(e.target.value)}
                    className="bg-transparent text-white font-black text-xs focus:outline-none cursor-pointer pr-1"
                  >
                    <option value="all" className="bg-neutral-900 text-white font-bold">Todas las Sedes</option>
                    {activeSedes.map((sede) => (
                      <option key={sede.uid} value={sede.uid} className="bg-neutral-900 text-white font-bold">
                        {sede.name || 'Club'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selector de Fecha Simplificado y Ordenado como solicitó el usuario */}
              <div className="flex flex-nowrap items-center bg-neutral-900/80 p-1 rounded-2xl w-full sm:w-auto overflow-hidden">
                {([
                  { value: '1day', label: 'Día' },
                  { value: '7days', label: 'Semana' },
                  { value: '30days', label: 'Mes' },
                  { value: 'all', label: 'Histórico' },
                  { value: 'custom', label: 'Personalizado' }
                ] as { value: DateRange; label: string }[]).map((r) => {
                  const active = selectedRange === r.value;
                  return (
                    <button
                      key={r.value}
                      onClick={() => setSelectedRange(r.value)}
                      className={`px-1.5 sm:px-4 py-1.5 sm:py-2 rounded-xl font-black text-[8.5px] sm:text-[11px] uppercase tracking-tighter sm:tracking-wider transition-all duration-300 flex-1 sm:flex-initial text-center whitespace-nowrap ${
                        active
                          ? 'theme-primary-bg text-black shadow-lg'
                          : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>

              {/* BOTÓN EXPORTAR EXCEL */}
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700 rounded-xl font-black text-[10px] sm:text-[11px] uppercase tracking-wider transition-all shrink-0 shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" width="14" height="14" className="text-[#21A366]">
                  <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm60.1 106.5L224 336l60.1 93.5c5.1 8-.6 18.5-10.1 18.5h-34.9c-4.4 0-8.5-2.4-10.6-6.3C208.9 405.5 192 373 192 373c-6.4 14.8-10 20-10 20l-28.9 56.4c-2.1 4.1-6.4 6.6-11.1 6.6H107c-9.5 0-15.2-10.4-10.1-18.4L160 336 99.8 242.5c-5-8 .6-18.5 10.1-18.5h34.9c4.4 0 8.5 2.4 10.6 6.3 19.6 36.2 36.6 68.7 36.6 68.7 6.4-14.8 10-20 10-20l28.9-56.4c2.1-4.1 6.4-6.6 11.1-6.6h34.9c9.6 0 15.3 10.5 10.2 18.5zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"/>
                </svg>
                Exportar
              </button>
            </div>

            {/* Date Pickers para rango personalizado - Ahora aparecen debajo en desktop para no mover el layout */}
            {selectedRange === 'custom' && (
              <div className="flex items-center justify-center gap-2 bg-black/60 border border-theme-primary/30 p-1.5 rounded-xl animate-in fade-in slide-in-from-top-2 w-full lg:w-auto self-end">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-neutral-900 border border-white/10 text-white text-[11px] font-bold rounded px-2 py-1 focus:outline-none focus:border-theme-primary"
                  style={{ colorScheme: 'dark' }}
                />
                <span className="text-white/50 text-xs font-bold">-</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-neutral-900 border border-white/10 text-white text-[11px] font-bold rounded px-2 py-1 focus:outline-none focus:border-theme-primary"
                  style={{ colorScheme: 'dark' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. INGRESOS - DESGLOSE TOTAL, CANCHAS Y CANTINA */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

        {/* INGRESO TOTAL + DESGLOSE */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-9 bg-[#1e212b] border border-[#3e4357] rounded-3xl relative overflow-hidden hover:border-lime-500/50 transition-all duration-300 shadow-lg"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-lime-500/5 blur-[60px] rounded-full pointer-events-none" />
          <div className="relative z-10">
            <div className="relative w-full p-6 pt-12 xl:pt-6 border-b border-[#3e4357] bg-white/[0.02] flex justify-center">
              <div className="absolute top-4 left-6">
                <h2 className="text-xs md:text-sm font-black text-zinc-500 uppercase tracking-widest">Balance</h2>
              </div>

              <div className="flex flex-wrap justify-center md:flex-nowrap items-center gap-6 xl:gap-10">
                <div>
                  <span className="text-white/40 text-[10px] font-black uppercase tracking-wider block mb-1">Ingresos Brutos</span>
                  <h3 className="text-xl md:text-2xl font-black text-white tracking-tight"><AnimatedNumber value={stats.totalRevenue} formatter={formatMoney} /></h3>
                </div>
                <div>
                  <span className="text-red-400/60 text-[10px] font-black uppercase tracking-wider block mb-1">Gastos Operativos</span>
                  <h3 className="text-xl md:text-2xl font-black text-red-400 tracking-tight">- <AnimatedNumber value={stats.operationalExpenses} formatter={formatMoney} /></h3>
                </div>
                <div className="border-t md:border-t-0 md:border-l border-[#3e4357]/50 pt-4 md:pt-0 md:pl-6 xl:pl-8">
                  <span className="text-lime-400/80 text-[10px] font-black uppercase tracking-wider block mb-1">Ingreso Neto</span>
                  <h3 className="text-2xl font-black text-lime-400 tracking-tight"><AnimatedNumber value={stats.netBalance} formatter={formatMoney} /></h3>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Barra Canchas */}
            <div className="space-y-1.5 mb-4">
              <div className="flex items-center justify-between text-xs font-bold">
                <div className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-yellow-400" /><span className="text-white/80">Alquiler de Canchas</span></div>
                <div className="flex items-center gap-2"><span className="text-white font-black"><AnimatedNumber value={stats.courtRevenue} formatter={formatMoney} /></span><span className="text-yellow-400 text-[10px] font-black bg-yellow-400/10 px-1.5 py-0.5 rounded-full"><AnimatedNumber value={stats.courtPct} formatter={(v) => `${v}%`} /></span></div>
              </div>
              <div className="h-3 w-full bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: `${stats.courtPct}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full shadow-[0_0_8px_rgba(250,204,21,0.3)]" 
                />
              </div>
            </div>

            {/* Barra Cantina */}
            <div className="space-y-1.5 mb-6">
              <div className="flex items-center justify-between text-xs font-bold">
                <div className="flex items-center gap-2"><Coffee className="w-3.5 h-3.5 text-blue-400" /><span className="text-white/80">Cantina & Productos</span></div>
                <div className="flex items-center gap-2"><span className="text-white font-black"><AnimatedNumber value={stats.cantinaRevenue} formatter={formatMoney} /></span><span className="text-blue-400 text-[10px] font-black bg-blue-400/10 px-1.5 py-0.5 rounded-full"><AnimatedNumber value={stats.cantinaPct} formatter={(v) => `${v}%`} /></span></div>
              </div>
              <div className="h-3 w-full bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: `${stats.cantinaPct}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.3)]" 
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Efectivo */}
              <div className="bg-[#222530] p-3 rounded-xl border border-white/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black uppercase text-zinc-400">Efectivo</span>
                  <span className="text-[10px] font-black bg-white/5 text-white/80 px-1.5 py-0.5 rounded-full"><AnimatedNumber value={stats.cashPct} formatter={(v) => `${v}%`} /></span>
                </div>
                <div className="text-sm font-black text-white"><AnimatedNumber value={stats.cashRevenue} formatter={formatMoney} /></div>
              </div>

              {/* Transferencia */}
              <div className="bg-[#222530] p-3 rounded-xl border border-white/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black uppercase text-zinc-400">Transferencia</span>
                  <span className="text-[10px] font-black bg-white/5 text-white/80 px-1.5 py-0.5 rounded-full"><AnimatedNumber value={stats.transferPct} formatter={(v) => `${v}%`} /></span>
                </div>
                <div className="text-sm font-black text-white"><AnimatedNumber value={stats.transferRevenue} formatter={formatMoney} /></div>
              </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* RESERVAS + TURNOS FIJOS */}
        <div className="lg:col-span-3 grid grid-rows-2 gap-5">
          {/* Reservas */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            className="bg-[#1e212b] border border-[#3e4357] rounded-3xl p-5 relative overflow-hidden hover:border-yellow-500/50 transition-all duration-300 shadow-lg"
          >
            <div className="absolute top-0 right-0 w-28 h-28 bg-yellow-500/5 blur-[40px] rounded-full pointer-events-none" />
            <div className="flex items-center justify-between relative z-10">
              <div>
                <span className="text-white/40 text-[10px] font-black uppercase tracking-wider block">Reservas</span>
                <h3 className="text-2xl md:text-3xl font-black text-white mt-1"><AnimatedNumber value={stats.totalBookings} /> <span className="text-white/30 text-sm font-bold">turnos</span></h3>
                <p className="text-white/35 text-[10px] font-medium mt-1">Turnos confirmados y pagados en el período</p>
              </div>
              <div className="p-2 rounded-xl bg-yellow-500/10 text-yellow-400"><CalendarDays className="w-5 h-5" /></div>
            </div>
          </motion.div>

          {/* Turnos Fijos */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            className="bg-[#1e212b] border border-[#3e4357] rounded-3xl p-5 relative overflow-hidden hover:border-amber-500/50 transition-all duration-300 shadow-lg"
          >
            <div className="absolute top-0 right-0 w-28 h-28 bg-amber-500/5 blur-[40px] rounded-full pointer-events-none" />
            <div className="flex items-center justify-between relative z-10">
              <div>
                <span className="text-white/40 text-[10px] font-black uppercase tracking-wider block">Turnos Fijos</span>
                <h3 className="text-2xl md:text-3xl font-black text-white mt-1"><AnimatedNumber value={stats.fixedBookingsCount} /> <span className="text-white/30 text-sm font-bold">activos</span></h3>
                <p className="text-white/35 text-[10px] font-medium mt-1">Turnos recurrentes semanales registrados</p>
              </div>
              <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
                <Pin className="w-5 h-5" />
              </div>
            </div>
          </motion.div>
        </div>

      </div>

      {/* 3. GRÁFICO DE TENDENCIA PRINCIPAL - GRANDE, CENTRADO Y ULTRA-APRECIABLE */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="bg-[#282d40] border border-[#3e4357] rounded-3xl p-6 md:p-8 relative shadow-lg"
      >
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 relative z-10">
          <div>
            <div className="flex items-center gap-2">
              <LineChart className="w-5 h-5 theme-primary-text" />
              <h3 className="text-base font-black uppercase tracking-wider text-white">Evolución de Ingresos y Reservas</h3>
            </div>
            <p className="text-white/40 text-xs font-semibold mt-1">
              {isDemoMode 
                ? "Visualización en modo demostración. Simulación de flujo de caja y demanda." 
                : "Flujo temporal de facturación y volumen de ocupación en el periodo seleccionado."}
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-1.5">
            <div className="flex flex-wrap items-center gap-4">
              <div 
                className={`flex items-center gap-2 cursor-pointer transition-all ${chartFilters.total ? 'opacity-100' : 'opacity-40 grayscale'}`}
                onClick={() => setChartFilters(prev => ({ ...prev, total: !prev.total }))}
              >
                <span className="w-3 h-3 rounded-full bg-[#84cc16] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                <span className="text-[11px] font-bold text-white/70 uppercase tracking-wider">Ingresos Totales</span>
              </div>
              <div 
                className={`flex items-center gap-2 cursor-pointer transition-all ${chartFilters.cantina ? 'opacity-100' : 'opacity-40 grayscale'}`}
                onClick={() => setChartFilters(prev => ({ ...prev, cantina: !prev.cantina }))}
              >
                <span className="w-3 h-3 rounded-full bg-[#3b82f6] shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                <span className="text-[11px] font-bold text-white/70 uppercase tracking-wider">Cantina ($)</span>
              </div>
              <div 
                className={`flex items-center gap-2 cursor-pointer transition-all ${chartFilters.turnos ? 'opacity-100' : 'opacity-40 grayscale'}`}
                onClick={() => setChartFilters(prev => ({ ...prev, turnos: !prev.turnos }))}
              >
                <span className="w-3 h-3 rounded-full bg-yellow-400" />
                <span className="text-[11px] font-bold text-white/70 uppercase tracking-wider">Turnos ($)</span>
              </div>
              {isDemoMode && (
                <span className="text-[9px] font-black bg-white/5 text-theme-primary border border-theme-primary/20 px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse">
                  Modo Demo
                </span>
              )}
            </div>
            <span className="text-[10px] text-white/50 italic font-medium animate-pulse">click para ocultar / mostrar</span>
          </div>
        </div>

        {/* SVG Vectorial Súper Nítido */}
        <div className="relative w-full overflow-hidden rounded-2xl bg-black/40 p-4">
          <svg 
            className="w-full h-auto" 
            viewBox={`0 0 ${chartSvgData.width} ${chartSvgData.height}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Defs para gradientes neon */}
            <defs>
              <linearGradient id="revenueAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#84cc16" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#84cc16" stopOpacity="0.0" />
              </linearGradient>
              <linearGradient id="revenueLineGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#84cc16" />
                <stop offset="50%" stopColor="#4ade80" />
                <stop offset="100%" stopColor="#84cc16" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Líneas de cuadrícula horizontal */}
            {chartSvgData.points.length > 0 && Array.from({ length: 5 }).map((_, i) => {
              const y = chartSvgData.paddingTop + (i / 4) * chartSvgData.chartHeight;
              const revenueValue = chartSvgData.maxRevenue - (i / 4) * chartSvgData.maxRevenue;
              return (
                <g key={i} className="opacity-20">
                  <line 
                    x1={chartSvgData.paddingLeft} 
                    y1={y} 
                    x2={chartSvgData.width - chartSvgData.paddingRight} 
                    y2={y} 
                    stroke="white" 
                    strokeWidth="1" 
                    strokeDasharray="4 4" 
                  />
                  <text 
                    x={chartSvgData.paddingLeft - 10} 
                    y={y + 4} 
                    fill="white" 
                    fontSize="9" 
                    fontWeight="bold"
                    textAnchor="end"
                  >
                    {formatMoney(revenueValue)}
                  </text>
                </g>
              );
            })}

            {/* Área sombreada para Ingresos */}
            {chartSvgData.points.length > 1 && chartFilters.total && (
              <>
                <path d={chartSvgData.revAreaPath} fill="url(#revenueAreaGrad)" className="transition-all duration-1000" />
                <path 
                  d={chartSvgData.revLinePath} 
                  stroke="url(#revenueLineGrad)" 
                  strokeWidth="3.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  filter="url(#glow)"
                  className="transition-all duration-1000"
                />
              </>
            )}

            {/* Línea de Turnos (Cian) */}
            {chartSvgData.courtLinePath && chartFilters.turnos && (
              <path 
                d={chartSvgData.courtLinePath} 
                stroke="#facc15" 
                strokeWidth="3.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="drop-shadow-[0_0_6px_rgba(250,204,21,0.4)] transition-all duration-500"
              />
            )}
            
            {/* Línea de Cantina (Verde) */}
            {chartSvgData.cantinaLinePath && chartFilters.cantina && (
              <path 
                d={chartSvgData.cantinaLinePath} 
                stroke="#3b82f6" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="drop-shadow-[0_0_6px_rgba(59,130,246,0.4)] transition-all duration-500"
              />
            )}

            {/* Etiquetas del eje X */}
            {chartSvgData.points.map((p, idx) => (
              <text 
                key={idx} 
                x={p.x} 
                y={chartSvgData.height - 15} 
                fill="white" 
                fillOpacity="0.4"
                fontSize="10" 
                fontWeight="black"
                textAnchor="middle"
              >
                {p.label}
              </text>
            ))}

            {/* Puntos de interacción (Círculos) */}
            {chartSvgData.points.map((p, idx) => {
              const isHovered = hoveredPointIdx === idx;
              return (
                <g 
                  key={idx} 
                  className="cursor-pointer group"
                  onMouseEnter={() => setHoveredPointIdx(idx)}
                  onMouseLeave={() => setHoveredPointIdx(null)}
                >
                  {/* Círculo invisible gigante para fácil hover táctil/mouse */}
                  <circle cx={p.x} cy={p.yRev} r="18" fill="transparent" />
                  
                  {/* Punto Ingresos */}
                  {chartFilters.total && (
                    <>
                      <circle 
                        cx={p.x} 
                        cy={p.yRev} 
                        r={isHovered ? "8" : "4.5"} 
                        fill="#84cc16" 
                        stroke="black"
                        strokeWidth="1.5"
                        className="transition-all duration-200"
                      />
                      {isHovered && (
                        <circle 
                          cx={p.x} 
                          cy={p.yRev} 
                          r="12" 
                          stroke="#84cc16" 
                          strokeWidth="1" 
                          strokeOpacity="0.3" 
                          fill="none" 
                          className="animate-pulse" 
                        />
                      )}
                    </>
                  )}

                  {/* Punto Cantina */}
                  {chartFilters.cantina && (
                    <circle 
                      cx={p.x} 
                      cy={p.yCantina} 
                      r={isHovered ? "6" : "3"} 
                      fill="#3b82f6" 
                      stroke="black"
                      strokeWidth="1.5"
                      className="transition-all duration-200"
                    />
                  )}

                  {/* Punto Turnos */}
                  {chartFilters.turnos && (
                    <circle 
                      cx={p.x} 
                      cy={p.yCourt} 
                      r={isHovered ? "7" : "3.5"} 
                      fill="#facc15" 
                      stroke="black"
                      strokeWidth="1.5"
                      className="transition-all duration-200"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Tooltip Dinámico Flotante en HTML para perfecta apreciación y legibilidad */}
          {hoveredPointIdx !== null && chartSvgData.points[hoveredPointIdx] && (() => {
            const pX = chartSvgData.points[hoveredPointIdx].x;
            const pY = chartSvgData.points[hoveredPointIdx].yRev;
            const percentX = (pX / chartSvgData.width) * 100;
            const percentY = (pY / chartSvgData.height) * 100;
            
            const isRightHalf = hoveredPointIdx >= chartSvgData.points.length / 2;
            const isTopNear = percentY < 25; // Si está muy arriba, el tooltip cae hacia abajo
            const isBottomNear = percentY > 75; // Si está muy abajo, el tooltip sube

            return (
              <div 
                className="absolute bg-neutral-950 border border-white/10 rounded-2xl p-4 shadow-2xl z-20 pointer-events-none animate-in fade-in zoom-in duration-150"
                style={{
                  left: `${Math.max(5, Math.min(95, percentX))}%`,
                  top: `${Math.max(10, Math.min(90, percentY))}%`,
                  transform: `translate(${isRightHalf ? 'calc(-100% - 15px)' : '15px'}, ${isTopNear ? '15px' : 'calc(-100% - 15px)'})`
                }}
              >
              <div className="space-y-1.5 min-w-[140px]">
                <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                  {chartSvgData.points[hoveredPointIdx].label}
                </p>
                <div className="pt-1 border-t border-white/5 space-y-1">
                  {chartFilters.total && (
                    <div className="flex items-center justify-between gap-4 text-xs font-bold">
                      <span className="text-white/60">Ingresos Totales:</span>
                      <span className="text-[#84cc16] font-black">
                        {formatMoney(chartSvgData.points[hoveredPointIdx].revenue)}
                      </span>
                    </div>
                  )}
                  {chartFilters.cantina && (
                    <div className="flex items-center justify-between gap-4 text-xs font-bold">
                      <span className="text-white/60">Cantina:</span>
                      <span className="text-[#3b82f6] font-black">
                        {formatMoney(chartSvgData.points[hoveredPointIdx].cantinaRevenue)}
                      </span>
                    </div>
                  )}
                  {chartFilters.turnos && (
                    <div className="flex items-center justify-between gap-4 text-xs font-bold">
                      <span className="text-white/60">Turnos:</span>
                      <span className="text-yellow-400 font-black">
                        {formatMoney(chartSvgData.points[hoveredPointIdx].courtRevenue)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </div>
      </motion.div>

      {/* 4. DÍAS DE MAYOR ACTIVIDAD Y USO POR CANCHA */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* GRÁFICO DE BARRAS VERTICALES: DÍAS DE LA SEMANA MÁS SOLICITADOS */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          className="bg-[#1e212b] border border-[#3e4357] rounded-3xl p-6 lg:col-span-7 flex flex-col justify-between shadow-lg"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4.5 h-4.5 theme-primary-text" />
              <h2 className="text-sm font-black uppercase tracking-wider text-white">Días de Mayor Actividad</h2>
            </div>
            <p className="text-white/40 text-xs font-semibold mb-8">
              Frecuencia de asistencia al club agrupada por día de la semana.
            </p>

            <div className="flex items-end justify-between gap-2 h-44 px-2">
              {weekdayDistribution.map((day) => (
                <div key={day.name} className="flex flex-col items-center flex-1 group">
                  
                  <div className="mb-2 bg-neutral-900 border border-white/10 text-white font-bold text-[10px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -translate-y-1">
                    {day.count} Res.
                  </div>

                  <div className="w-full max-w-[28px] bg-white/5 rounded-t-lg h-32 relative overflow-hidden flex items-end">
                    <motion.div
                      initial={{ height: 0 }}
                      whileInView={{ height: `${day.scale}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="w-full theme-primary-bg rounded-t-lg group-hover:brightness-110 shadow-[0_0_8px_rgba(var(--glow-rgb),0.3)]"
                    />
                  </div>

                  <span className="text-[10px] font-black uppercase tracking-wider text-white/50 group-hover:text-white transition-colors duration-300 mt-3">
                    {day.name}
                  </span>
                  
                  <span className="text-[8px] font-bold theme-primary-text mt-0.5">
                    <AnimatedNumber value={day.percentage} formatter={(v) => `${v}%`} />
                  </span>

                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5 pt-4 mt-6 flex items-center justify-center gap-2 text-[10px] text-white/40 font-bold uppercase tracking-wider">
            <span>Día de mayor flujo:</span>
            <span className="theme-primary-text font-black">
              {weekdayDistribution.reduce((max, d) => d.count > max.count ? d : max, weekdayDistribution[0])?.name || 'N/A'}
            </span>
          </div>
        </motion.div>

        {/* GRÁFICO DE BARRAS HORIZONTALES: CANCHAS MÁS RESERVADAS */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          className="bg-[#1e212b] border border-[#3e4357] rounded-3xl p-6 lg:col-span-5 flex flex-col justify-between shadow-lg"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4.5 h-4.5 theme-primary-text" />
              <h2 className="text-sm font-black uppercase tracking-wider text-white">Uso por Cancha</h2>
            </div>
            <p className="text-white/40 text-xs font-semibold mb-6">
              Distribución de turnos reservados por cada una de las canchas.
            </p>

            <div className="space-y-5">
              {courtDistribution.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-white/30 text-xs">
                  <Calendar className="w-8 h-8 mb-2 opacity-50" />
                  No hay datos de canchas para este periodo.
                </div>
              ) : (
                courtDistribution.map((court, idx) => {
                  const activeColor = 'bg-yellow-400';

                  return (
                    <div key={court.name} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs font-bold">
                        <span className="text-white/90">{court.name}</span>
                        <div className="space-x-2 text-white/40">
                          <span className="text-white/80"><AnimatedNumber value={court.count} /> turnos</span>
                          <span>•</span>
                          <span className="theme-primary-text font-black"><AnimatedNumber value={court.percentage} formatter={(v) => `${v}%`} /></span>
                        </div>
                      </div>
                      
                      <div className="h-2.5 w-full bg-white/5 rounded-full overflow-hidden relative">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${court.percentage}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className={`h-full rounded-full ${activeColor}`}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="border-t border-white/5 pt-4 mt-6 flex items-center justify-center gap-2 text-[10px] text-white/40 font-bold uppercase tracking-wider">
            <span>Cancha preferida:</span>
            <span className="theme-primary-text font-black">
              {courtDistribution[0]?.name || 'N/A'}
            </span>
          </div>
        </motion.div>

      </div>

      {/* 5 & 6. UNIFICACIÓN: HORAS PICO Y RANKING DE FIDELIDAD */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* HORAS PICO DE DEMANDA */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          className="lg:col-span-12 bg-[#1e212b] border border-[#3e4357] rounded-3xl p-6 flex flex-col justify-between shadow-lg"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4.5 h-4.5 theme-primary-text" />
              <h2 className="text-sm font-black uppercase tracking-wider text-white">Turnos más elegidos</h2>
            </div>
            <p className="text-white/40 text-xs font-semibold mb-8">
              Frecuencia de reservas agrupada por horario del turno.
            </p>

            {hourDistribution.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-white/30 text-xs">
                <Clock className="w-8 h-8 mb-2 opacity-50" />
                No hay datos para este periodo.
              </div>
            ) : (
              <div className="flex items-end justify-between gap-1.5 h-44 px-2">
                {hourDistribution.map((item) => {
                  const totalCounts = hourDistribution.reduce((acc, h) => acc + h.count, 0);
                  const percentage = totalCounts > 0 ? Math.round((item.count / totalCounts) * 100) : 0;
                  
                  return (
                    <div key={item.hour} className="flex flex-col items-center flex-1 group">
                      
                      <div className="mb-2 bg-neutral-900 border border-white/10 text-white font-bold text-[10px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -translate-y-1 whitespace-nowrap z-20">
                        {item.count} Res.
                      </div>

                      <div className="w-full max-w-[24px] bg-white/5 rounded-t-lg h-32 relative overflow-hidden flex items-end">
                        <motion.div
                          initial={{ height: 0 }}
                          whileInView={{ height: `${item.scale}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="w-full theme-primary-bg rounded-t-lg group-hover:brightness-110 shadow-[0_0_8px_rgba(var(--glow-rgb),0.3)]"
                        />
                      </div>

                      <span className="text-[9px] font-black uppercase tracking-tighter text-white/50 group-hover:text-white transition-colors duration-300 mt-3">
                        {item.hour.split(':')[0]}
                      </span>
                      
                      <span className="text-[8px] font-bold theme-primary-text mt-0.5">
                        <AnimatedNumber value={percentage} formatter={(v) => `${v}%`} />
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-white/5 pt-4 mt-6 flex items-center justify-center gap-2 text-[10px] text-white/40 font-bold uppercase tracking-wider">
            <span>Turno más elegido:</span>
            <span className="theme-primary-text font-black">
              {hourDistribution.reduce((max, h) => h.count > max.count ? h : max, hourDistribution[0])?.hour || 'N/A'} HS
            </span>
          </div>
        </motion.div>

        {/* RANKING DE FIDELIDAD (EXPANDIDO) */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          className="lg:col-span-12 bg-[#1e212b] border border-[#3e4357] rounded-3xl p-6 flex flex-col shadow-lg"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Award className="w-4.5 h-4.5 theme-primary-text" />
              <h2 className="text-sm font-black uppercase tracking-wider text-white">Fidelidad de clientes</h2>
            </div>
            {topPlayers.length > 3 && (
              <button 
                onClick={() => setShowFidelityModal(true)}
                className="text-[10px] font-black text-theme-primary uppercase tracking-widest hover:underline flex items-center gap-1"
              >
                Ver más <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
          <p className="text-white/40 text-[10px] font-semibold mb-6">
            Clientes con mayor número de reservas.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
            {topPlayers.length === 0 ? (
              <div className="lg:col-span-5 flex flex-col items-center justify-center py-8 text-white/30 text-xs">
                <Users className="w-8 h-8 mb-2 opacity-50" />
                Sin registros.
              </div>
            ) : (
              topPlayers.slice(0, 5).map((player, idx) => {
                const medalColors = [
                  'theme-primary-text',
                  'text-slate-300',
                  'text-amber-600',
                  'text-white/40',
                  'text-white/40'
                ];

                return (
                  <div 
                    key={player.id} 
                    className="flex items-center gap-3 p-4 rounded-2xl bg-black/35 border border-white/5 hover:border-white/10 transition-all duration-300 group"
                  >
                    <div className={`w-8 h-8 shrink-0 flex items-center justify-center text-xs font-black rounded-full bg-white/5 ${medalColors[idx]}`}>
                      #{idx + 1}
                    </div>
                    
                    <div className="relative shrink-0">
                      {player.photo ? (
                        <img
                          src={player.photo}
                          alt={player.name}
                          className="w-11 h-11 rounded-full object-cover border-2 border-white/10 group-hover:border-theme-primary/40 transition-colors"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center text-white/50 text-sm font-black group-hover:border-theme-primary/40 transition-colors">
                          {player.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-black text-white truncate">{player.name}</p>
                      <div className="flex flex-col mt-0.5">
                        <span className="text-[10px] font-black theme-primary-text"><AnimatedNumber value={player.count} /> Reservas</span>
                        <span className="text-[10px] text-lime-400 font-bold"><AnimatedNumber value={player.spend} formatter={formatMoney} /></span>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </motion.div>
      </div>

      {/* 7. DESGLOSE DE CANTINA Y PRODUCTOS ADICIONALES */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="bg-[#1e212b] border border-[#3e4357] rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-xl"
      >
        <div className="absolute top-0 left-0 w-40 h-40 bg-blue-500/5 blur-[60px] rounded-full pointer-events-none" />
        
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 relative z-10 border-b border-white/5 pb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black uppercase tracking-wider text-white">Consumo en Cantina & Productos</h2>
              <p className="text-white/40 text-xs font-medium mt-0.5">
                Desglose de artículos, pelotas, paletas y bebidas adquiridos junto a las reservas de canchas.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-black/40 border border-white/5 px-4 py-2.5 rounded-2xl shrink-0 w-full md:w-auto justify-between md:justify-start">
            <div className="text-left">
              <span className="text-[9px] font-bold text-white/40 uppercase block">Total</span>
              <span className="text-sm font-black text-blue-400"><AnimatedNumber value={totalProductsRevenue} formatter={formatMoney} /></span>
            </div>
            <div className="h-6 w-[1px] bg-white/10" />
            <div className="text-right md:text-left">
              <span className="text-[9px] font-bold text-white/40 uppercase block">Unidades</span>
              <span className="text-sm font-black text-white"><AnimatedNumber value={totalProductsSold} /> items</span>
            </div>
          </div>
        </div>

        {/* Listado / Grid de Productos Compacto y Profesional */}
        {topProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-white/30 text-xs">
            <Package className="w-8 h-8 mb-2 opacity-40 animate-bounce" />
            No se registraron ventas de artículos o cantina en el periodo seleccionado.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
            {topProducts.map((product, idx) => (
              <div 
                key={product.name} 
                className="bg-black/40 border border-white/5 hover:border-blue-500/20 p-4 rounded-2xl flex items-center justify-between gap-3 transition-all duration-300 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-black shrink-0 group-hover:scale-105 transition-transform">
                    #{idx + 1}
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-black text-white truncate">{product.name}</h4>
                    <span className="text-[10px] text-white/40 font-medium block mt-0.5">
                      {product.count} {product.count === 1 ? 'unidad vendida' : 'unidades vendidas'}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs font-black text-blue-400 block">
                    <AnimatedNumber value={product.revenue} formatter={formatMoney} />
                  </span>
                  <span className="text-[9px] text-white/30 font-bold uppercase block mt-0.5">
                    Subtotal
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-white/5 pt-4 mt-6 flex items-center justify-between text-[10px] text-white/40 font-bold uppercase tracking-wider relative z-10">
          <span>Catálogo de Ventas Adicionales</span>
          <span>{topProducts.length} productos distintos listados</span>
        </div>
      </motion.div>

      {/* 8. MODAL DE FIDELIDAD DE CLIENTES (TOP 20) */}
      {showFidelityModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-neutral-900 border border-white/10 w-full max-w-2xl rounded-[32px] overflow-hidden shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in duration-300">
            {/* Header Modal */}
            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-black/20">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-theme-primary/10 theme-primary-text">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white uppercase tracking-tight">Fidelidad de Clientes</h3>
                  <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest mt-0.5">Top 20 jugadores con más reservas</p>
                </div>
              </div>
              <button 
                onClick={() => setShowFidelityModal(false)}
                className="p-2 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Listado Modal */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {topPlayers.map((player, idx) => {
                  const medalColors = [
                    'theme-primary-text',
                    'text-slate-300',
                    'text-amber-600',
                    'text-white/40',
                    'text-white/40'
                  ];

                  return (
                    <div 
                      key={player.id} 
                      className="flex items-center gap-4 p-4 rounded-2xl bg-black/40 border border-white/5 hover:border-white/10 transition-all duration-300 group"
                    >
                      <div className={`w-8 h-8 shrink-0 flex items-center justify-center text-[11px] font-black ${medalColors[idx] || 'text-white/20'}`}>
                        #{idx + 1}
                      </div>
                      
                      <div className="relative shrink-0">
                        {player.photo ? (
                          <img
                            src={player.photo}
                            alt={player.name}
                            className="w-12 h-12 rounded-full object-cover border border-white/10 group-hover:border-theme-primary/40 transition-colors"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 text-sm font-black group-hover:border-theme-primary/40 transition-colors">
                            {player.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-black text-white truncate">{player.name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-black theme-primary-text"><AnimatedNumber value={player.count} /> Reservas</span>
                          <span className="text-[10px] text-lime-400 font-bold"><AnimatedNumber value={player.spend} formatter={formatMoney} /></span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer Modal */}
            <div className="p-4 border-t border-white/5 bg-black/20 flex justify-center">
              <button 
                onClick={() => setShowFidelityModal(false)}
                className="px-6 py-2 rounded-xl bg-white/10 text-white/80 font-black text-[10px] uppercase tracking-widest hover:bg-white/20 hover:text-white transition-all"
              >
                Cerrar Ventana
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
